import React from 'react'
import { Route, Routes, useLocation, matchPath } from 'react-router-dom'
import Dashboard from '@/pages/dashboard/Dashboard.tsx'
import Users from '@/pages/users/Users.tsx'
import Login from '@/pages/auth/Login.tsx'
import PrivateRoute from '@/component/util/PrivateRoute.tsx'
import Account from '@/pages/account/Account.tsx'
import HomeRoute from '@/component/util/HomeRoute.tsx'
import GuidePage from '@/pages/landing/Guide/GuidePage.tsx'
import UserDetailMapPage from '@/pages/users/detail/UserDetailMapPage.tsx'
import DevPage from '@/pages/etc/DevPage.tsx'
import NotificationPage from '@/pages/notification/NotificationPage.tsx'
import MentalHealthDashboard from '@/pages/mentalHealth/MentalHealthDashboard.tsx'
import MentalHealthReports from '@/pages/mentalHealth/MentalHealthReports.tsx'
import MentalHealthReportDetail from '@/pages/mentalHealth/MentalHealthReportDetail.tsx'
import Home from '@/pages/landing/Home/Home'
import TermsPage from '@/pages/policy/TermsPage.tsx'
import PrivacyPage from '@/pages/policy/PrivacyPage.tsx'
import LocationTermsPage from '@/pages/policy/LocationTermsPage.tsx'
import LocationPrivacyPage from '@/pages/policy/LocationPrivacyPage.tsx'
import { useAuth } from '@/hooks/useAuth'
import { useAuthInit } from '@/hooks/useAuthInit'
import GlobalAlertManager from '@/component/util/GlobalAlertManager'
import AlertLiveStreamManager from '@/component/util/AlertLiveStreamManager'
import TestSessionManager from '@/component/util/TestSessionManager'
import FullPageLoading from '@/component/util/FullPageLoading'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

const ROUTE_CONFIG = [
    { path: '/', element: <HomeRoute />, isPublic: true },
    { path: '/home', element: <Home />, isPublic: true },
    { path: '/guide', element: <GuidePage />, isPublic: true },
    { path: '/login', element: <Login />, isPublic: true },
    { path: '/terms', element: <TermsPage />, isPublic: true },
    { path: '/privacy', element: <PrivacyPage />, isPublic: true },
    { path: '/location-terms', element: <LocationTermsPage />, isPublic: true },
    { path: '/location-privacy', element: <LocationPrivacyPage />, isPublic: true },
    { path: '/dashboard', element: <Dashboard /> },
    { path: '/user', element: <Users /> },
    { path: '/user/:id/map', element: <UserDetailMapPage /> },
    { path: '/notification', element: <NotificationPage /> },
    { path: '/mental-health', element: <MentalHealthDashboard /> },
    { path: '/mental-health/reports', element: <MentalHealthReports /> },
    // 정본 경로. 서버 조회에 대상자와 세션이 둘 다 필요하다.
    {
        path: '/mental-health/protectee/:protecteeId/reports/:sessionId',
        element: <MentalHealthReportDetail />
    },
    // 구 경로. ?userId= 가 있으면 정본으로 넘긴다 — 북마크가 죽지 않게 한 릴리스 남겨 둔다.
    { path: '/mental-health/reports/:id', element: <MentalHealthReportDetail /> },
    { path: '/account', element: <Account /> },
    { path: '/dev', element: <DevPage /> }
]

const wrapPrivateRoute = (element: React.ReactNode, href = '/login') => (
    <PrivateRoute href={href}>{element}</PrivateRoute>
)

export const AppRoutes: React.FC = () => {
    const location = useLocation()
    const { isLoggedIn } = useAuth()
    useDocumentTitle()

    const currentRouteConfig = ROUTE_CONFIG.find((route) =>
        matchPath(route.path, location.pathname)
    )
    const isPublicRoute = currentRouteConfig?.isPublic ?? false

    // 공용 페이지가 아닐 때 혹은 로그인 페이지일 때만 인증 초기화(Refresh) 시도
    const shouldCheckAuth = !isPublicRoute || location.pathname === '/login'

    const isAuthInitialized = useAuthInit({ enabled: shouldCheckAuth })
    if (!isAuthInitialized && shouldCheckAuth) {
        return <FullPageLoading />
    }

    return (
        <>
            {/* 로그인 상태이고 공용 경로가 아닐 때만 알림 매니저 작동 */}
            {isLoggedIn && !isPublicRoute && <GlobalAlertManager />}
            {isLoggedIn && !isPublicRoute && <AlertLiveStreamManager />}
            {isLoggedIn && !isPublicRoute && <TestSessionManager />}

            <Routes>
                {ROUTE_CONFIG.map(({ path, element, isPublic }) => (
                    <Route
                        key={path}
                        path={path}
                        element={isPublic ? element : wrapPrivateRoute(element)}
                    />
                ))}
            </Routes>
        </>
    )
}
